// src/domain/status.ts
// Canonical status vocabulary (design brief §5.2). Every status string in the product
// comes from here and renders through <StatusPill>. No screen invents a synonym, and
// colour words (Green/Amber/Red) are never used as labels.
import type { SpendState, AIReviewState, SpendRequest, Milestone } from "./types";

/**
 * Order a track's milestones for sequence reasoning: by explicit `order` when every
 * milestone carries one, otherwise the array order they were authored in.
 */
export function orderMilestones<T extends Pick<Milestone, "order">>(track: T[]): T[] {
  return track.every((m) => typeof m.order === "number")
    ? [...track].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : track;
}

/**
 * The single "current" milestone of a track (spec §3.3 — "the current milestone is
 * highlighted"; §11 — a linear sequence with one current point and "downstream" ones):
 * the earliest milestone not yet disbursed or closed. Returns `undefined` when the whole
 * track is complete. A track is one project-level *or* one sub-project sequence — never the
 * two mixed — so callers pass an already-scoped list. Deriving this (rather than storing a
 * hand-maintained flag) keeps the one-current-per-track invariant true even when milestones
 * are disbursed out of order.
 */
export function currentMilestoneId(track: Pick<Milestone, "id" | "status" | "order">[]): string | undefined {
  return orderMilestones(track).find((m) => m.status !== "disbursed" && m.status !== "closed")?.id;
}

/**
 * A spend ask has an "open query" when a reviewer has queried it (spec §6.3 — the query
 * "sends back to Reporter for more info") and the Reporter has not yet replied. The ask
 * stays `proposed` (recoverable, still in the approver's queue); we derive the queried
 * sub-state from the discussion thread: the most recent entry is a `query`. Once the
 * Reporter replies (a `comment` entry), it is no longer open — it is back to
 * "Waiting for approval" for the approver to re-decide.
 */
export function hasOpenQuery(ask: Pick<SpendRequest, "state" | "thread">): boolean {
  if (ask.state !== "proposed") return false;
  const last = ask.thread?.[ask.thread.length - 1];
  return last?.kind === "query";
}

export type StatusTone = "success" | "warning" | "danger" | "info" | "pending";
export type StatusDef = { label: string; tone: StatusTone };

/** Project / programme / milestone-pace health. Replaces all Green/Amber/Red wording. */
export const HEALTH: Record<"on_track" | "watch" | "at_risk", StatusDef> = {
  on_track: { label: "On track", tone: "success" },
  watch: { label: "Watch", tone: "warning" },
  at_risk: { label: "At risk", tone: "danger" },
};

export function healthFromRag(rag: "GREEN" | "AMBER" | "RED"): StatusDef {
  return rag === "GREEN" ? HEALTH.on_track : rag === "AMBER" ? HEALTH.watch : HEALTH.at_risk;
}

/** Spend ask lifecycle. */
export const ASK_STATE: Record<SpendState, StatusDef> = {
  proposed: { label: "Proposed", tone: "pending" },
  approved_for_spend: { label: "Approved for spend", tone: "info" },
  committed: { label: "Committed", tone: "info" },
  evidence_submitted: { label: "Evidence submitted", tone: "info" },
  confirmed: { label: "Confirmed", tone: "success" },
  flagged: { label: "Queried", tone: "warning" },
  rejected: { label: "Rejected", tone: "danger" },
};

/**
 * Reporter ("field voice") wording for the same lifecycle. The field user reads the spend
 * as a task ("Waiting for approval", "Ready to spend", "Paid"), not as the system's
 * accounting term — so the card label never contradicts the plain filter chips. Same tone
 * (and therefore same colour + indicator) as ASK_STATE, only the words change.
 */
export const ASK_STATE_REPORTER: Record<SpendState, StatusDef> = {
  proposed: { label: "Waiting for approval", tone: "pending" },
  approved_for_spend: { label: "Ready to spend", tone: "info" },
  committed: { label: "Waiting to be confirmed", tone: "info" },
  evidence_submitted: { label: "Waiting to be confirmed", tone: "info" },
  confirmed: { label: "Paid", tone: "success" },
  flagged: { label: "Queried", tone: "warning" },
  rejected: { label: "Not approved", tone: "danger" },
};

/** Milestone disbursement. */
export const DISBURSEMENT_STATE: Record<
  "queued_for_stratus" | "ready_for_funder" | "approved" | "completed" | "queried" | "held",
  StatusDef
> = {
  queued_for_stratus: { label: "Routed", tone: "info" },
  ready_for_funder: { label: "Routed", tone: "info" },
  approved: { label: "Approved", tone: "success" },
  completed: { label: "Disbursed", tone: "success" },
  queried: { label: "Queried", tone: "warning" },
  held: { label: "Held", tone: "warning" },
};

/** Heimdall (AI Analyst) review badge. */
export const REVIEW_STATE: Record<AIReviewState, StatusDef> = {
  passed: { label: "Checks passed", tone: "success" },
  flagged: { label: "Flags raised", tone: "warning" },
  pending: { label: "Not yet reviewed", tone: "pending" },
};

/** Entity (organisation / project / sub-project). */
export const ENTITY_STATE: Record<string, StatusDef> = {
  active: { label: "Active", tone: "success" },
  planned: { label: "Planned", tone: "pending" },
  on_hold: { label: "On hold", tone: "warning" },
  suspended: { label: "Suspended", tone: "danger" },
  revoked: { label: "Suspended", tone: "danger" },
  closed: { label: "Closed", tone: "pending" },
  // ZEUS governance statuses (project.status): draft -> review -> published,
  // with archived as the terminal state. Distinct from the legacy
  // active/planned/closed values above, which older seed data still uses.
  draft: { label: "Draft", tone: "pending" },
  review: { label: "In review", tone: "warning" },
  published: { label: "Published", tone: "success" },
  archived: { label: "Archived", tone: "pending" },
};

/**
 * Statuses that count as "active"/ongoing for portfolio-level stats and filters
 * (as opposed to not-yet-started or terminal). Covers both the legacy vocabulary
 * (active) and the ZEUS governance vocabulary (review, published) — a project
 * under governance review still has live operational activity (spend, evidence,
 * milestones aren't gated by project.status).
 */
const OPEN_PROJECT_STATUSES = new Set(["active", "review", "published"]);

export function isOpenProjectStatus(status: string): boolean {
  return OPEN_PROJECT_STATUSES.has(status);
}
